/* ─────────────────────────────────────────────────────────────
   TANGIG — Single Source of Truth
   Changer une valeur ici affecte TOUT le site.
───────────────────────────────────────────────────────────── */

/* ── Core palette ────────────────────────────────────────────── */
export const WHITE      = "#FFFFFF";
export const BLACK      = "#0A0A0A";
export const BLUE       = "#0071E3";   // Apple blue — primary accent
export const TEAL       = BLUE;        // alias kept for backwards compat
export const GREY       = "#F5F5F7";
export const MUTED      = "#6E6E73";
export const BORDER     = "rgba(0,0,0,0.08)";
export const BORDER_MD  = "rgba(0,0,0,0.12)";
export const VIOLET     = "#7C3AED";   // FAQ / help center accent

/* ── Gradients ───────────────────────────────────────────────── */
export const GRADIENT = {
  /* Text gradient — apply as background-clip: text */
  text:      "linear-gradient(135deg, #0071E3 0%, #40AAFF 100%)",
  textWarm:  "linear-gradient(135deg, #0071E3 0%, #34D1BF 100%)",

  /* Background gradients */
  hero:      "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(0,113,227,0.10) 0%, transparent 65%)",
  section:   "linear-gradient(180deg, rgba(0,113,227,0.04) 0%, transparent 100%)",
  blueTint:  "linear-gradient(135deg, rgba(0,113,227,0.06) 0%, rgba(64,170,255,0.04) 100%)",

  /* Button gradient */
  btn:       "linear-gradient(135deg, #0071E3 0%, #0088FF 100%)",
  btnHover:  "linear-gradient(135deg, #005BBF 0%, #006FDD 100%)",

  /* Glow */
  glow:      "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(0,113,227,0.15) 0%, transparent 70%)",
};

/* ── Semantic color map (use these in components) ────────────── */
export const colors = {
  /* Backgrounds */
  background:       WHITE,
  backgroundAlt:    GREY,
  backgroundDark:   "#000000",
  backgroundCard:   "#FAFBFA",
  backgroundSubtle: "#F7F7F8",
  backgroundBlue:   "rgba(0,113,227,0.05)",   // very light blue tint

  /* Text */
  textPrimary:   BLACK,
  textSecondary: MUTED,
  textMuted:     "#AEAEB2",
  textInverse:   WHITE,
  textAccent:    BLUE,

  /* Accent */
  accent:        BLUE,
  accentHover:   "#005BBF",
  accentSubtle:  "rgba(0,113,227,0.08)",
  accentGlow:    "rgba(0,113,227,0.20)",

  /* Borders */
  border:        BORDER,
  borderMedium:  BORDER_MD,
  borderStrong:  "rgba(0,0,0,0.18)",
  borderAccent:  "rgba(0,113,227,0.20)",

  /* Misc */
  cardAlt:       "#F0F7FF",   // blue-tint card surface
};

/* ── Typography ──────────────────────────────────────────────── */
export const FONT           = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
export const LETTER_SPACING = "-0.03em";

export const TYPE = {
  h1:    "clamp(40px, 5.5vw, 72px)",
  h2:    "clamp(28px, 3.5vw, 48px)",
  h3:    "24px",
  body:  "17px",
  small: "14px",
  micro: "11px",
};

export const WEIGHT = {
  bold:    700,
  semi:    600,
  medium:  500,
  regular: 400,
};

/* ── Spacing ─────────────────────────────────────────────────── */
export const SPACING = {
  sectionY:     "160px",
  sectionYSm:   "120px",
  containerMax: "1280px",
  containerMd:  "1200px",
  containerSm:  "900px",
};

/* ── Borders & radius ────────────────────────────────────────── */
export const RADIUS = {
  sm:   "10px",
  md:   "16px",
  lg:   "24px",
  xl:   "32px",
  pill: "99px",
  full: "50%",
};

/* ── Shadows ─────────────────────────────────────────────────── */
export const SHADOW = {
  card:      "0 4px 40px rgba(0,0,0,0.06), 0 1px 8px rgba(0,0,0,0.04)",
  cardHover: "0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)",
  navbar:    "0 1px 20px rgba(0,0,0,0.05)",
  modal:     "0 8px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
  blue:      "0 8px 32px rgba(0,113,227,0.25), 0 2px 8px rgba(0,113,227,0.15)",
  blueGlow:  "0 0 40px rgba(0,113,227,0.20)",
};

/* ── Easing & transitions ────────────────────────────────────── */
export const EASE       = "cubic-bezier(0.16, 1, 0.3, 1)";
export const TRANSITION = {
  fast:     `0.15s ${EASE}`,
  base:     `0.3s ${EASE}`,
  slow:     `0.6s ${EASE}`,
};

/* ── Button presets ──────────────────────────────────────────── */
export const btnPrimary = {
  display:         "inline-flex",
  alignItems:      "center",
  justifyContent:  "center",
  background:      GRADIENT.btn,
  color:           WHITE,
  height:          "52px",
  paddingLeft:     "28px",
  paddingRight:    "28px",
  borderRadius:    RADIUS.pill,
  fontWeight:      WEIGHT.semi,
  fontSize:        TYPE.small,
  fontFamily:      FONT,
  textDecoration:  "none",
  border:          "none",
  cursor:          "pointer",
  boxShadow:       SHADOW.blue,
  transition:      `background ${TRANSITION.base}, box-shadow ${TRANSITION.base}`,
};

export const btnSecondary = {
  display:         "inline-flex",
  alignItems:      "center",
  justifyContent:  "center",
  backgroundColor: "transparent",
  color:           BLACK,
  height:          "52px",
  paddingLeft:     "28px",
  paddingRight:    "28px",
  borderRadius:    RADIUS.pill,
  fontWeight:      WEIGHT.medium,
  fontSize:        TYPE.small,
  fontFamily:      FONT,
  textDecoration:  "none",
  border:          `1px solid ${colors.borderStrong}`,
  cursor:          "pointer",
  transition:      `background-color ${TRANSITION.base}`,
};

/* ── Dot bullet ─────────────────────────────────────────────── */
export function Dot() {
  return (
    <div
      style={{
        width:           "6px",
        height:          "6px",
        borderRadius:    RADIUS.full,
        backgroundColor: BLUE,
        flexShrink:      0,
      }}
    />
  );
}

/* ── Legacy flat exports (keep home/* working) ───────────────── */
export { BLUE as GREEN };   // IAPage used GREEN as alias
